// ---------------------------------------------------------------------------
// DSS shared: Reusable filter slider row — label + slider + number + reset
// Used by DSS-15 (filter-controls) and DSS-16 (backdrop-filter-controls)
// ---------------------------------------------------------------------------
import { useId } from "react";
import { RotateCcw } from "lucide-react";

// ── Types ──
export interface FilterSliderRowProps {
  readonly label: string;
  readonly value: number;
  readonly defaultValue: number;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly unit: string;
  readonly onChange: (value: number) => void;
}

// ── Utility ──
function safeNumber(raw: number, fallback: number): number {
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(safeNumber(val, min), min), max);
}

// ── Component ──
export default function FilterSliderRow({
  label,
  value,
  defaultValue,
  min,
  max,
  step = 1,
  unit,
  onChange,
}: FilterSliderRowProps) {
  const uid = useId();
  const sliderId = `${uid}-slider`;

  const isDefault = value === defaultValue;

  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={sliderId}
        className="w-20 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
      >
        {label}
      </label>
      <input
        id={sliderId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
        className="h-6 w-14 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-right text-[10px] tabular-nums text-[var(--color-foreground)]"
      />
      <span className="w-5 text-[10px] text-[var(--color-muted-foreground)]">
        {unit}
      </span>
      <button
        type="button"
        onClick={() => onChange(defaultValue)}
        disabled={isDefault}
        aria-label={`Reset ${label}`}
        className={[
          "flex h-5 w-5 shrink-0 items-center justify-center rounded",
          isDefault
            ? "text-[var(--color-muted-foreground)]/30 cursor-default"
            : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
        ].join(" ")}
      >
        <RotateCcw className="h-3 w-3" />
      </button>
    </div>
  );
}
